import React, { useState, useCallback, useEffect } from 'react';
import { PracticeScreen, KPracticeSettings } from '../types';
import IDoScreen from './IDoScreen';
import WeDoScreen from './WeDoScreen';
import YouDoScreen from './YouDoScreen';
import AccuracyScreen from './AccuracyScreen';
import Fluency15Screen from './Fluency15Screen';
import FluencyScreen from './FluencyScreen';
import { TRACK_RANGES } from '../../../types/constants';
import { AdditionFactWithId } from './types';

interface AdditionWithin5PracticeProps {
  settings: KPracticeSettings;
  factsToPractice: string[];
  onComplete: () => void;
  onBack: () => void;
  initialPracticeType?: 'learning' | 'accuracy' | 'fluency15' | 'fluency';
}

const AdditionWithin5Practice: React.FC<AdditionWithin5PracticeProps> = ({
  settings,
  factsToPractice,
  onComplete,
  onBack,
  initialPracticeType = 'learning'
}) => {
  // Determine initial screen based on practice type
  const getInitialScreen = (): PracticeScreen => {
    switch (initialPracticeType) {
      case 'accuracy':
        return 'accuracy';
      case 'fluency15':
        return 'fluency15';
      case 'fluency':
        return 'fluency';
      default:
        return 'ido';
    }
  };

  const [currentScreen, setCurrentScreen] = useState<PracticeScreen>(getInitialScreen());
  const [currentFactIndex, setCurrentFactIndex] = useState(0);

  // Convert fact ID to index into ADDITION_FACTS array
  // FACT1685 = index 0, FACT1686 = index 1, ..., FACT1696 = index 11
  const factToIndex = useCallback((factId: string): number => {
    const factNumber = parseInt(factId.replace('FACT', ''));
    const [startFact] = TRACK_RANGES.TRACK_K_ADDITION_WITHIN_5;
    return factNumber - startFact;
  }, []);

  // Generate all 12 K Addition Within 5 facts (FACT1685 to FACT1696)
  const getAllTrackFacts = useCallback((): AdditionFactWithId[] => {
    const [startFact, endFact] = TRACK_RANGES.TRACK_K_ADDITION_WITHIN_5;
    const allFacts: AdditionFactWithId[] = [];
    for (let factNum = startFact; factNum <= endFact; factNum++) {
      allFacts.push({
        factId: `FACT${factNum}`,
        factIndex: factNum - startFact // 0, 1, 2, ..., 11
      });
    }
    return allFacts;
  }, []);

  const renderCurrentScreen = () => {
    switch (currentScreen) {
      case 'ido':
        return (
          <IDoScreen
            settings={settings}
            factsToPractice={factsToPractice}
            onComplete={() => {
              setCurrentScreen('wedo');
              setCurrentFactIndex(0);
            }}
            onBack={onBack}
          />
        );

      case 'wedo': {
        // Convert facts to required format
        const factsWithIndices = factsToPractice.map(factId => ({
          factId,
          factIndex: factToIndex(factId)
        }));
        return (
          <WeDoScreen
            settings={settings}
            factsToPractice={factsWithIndices}
            onComplete={() => {
              setCurrentScreen('youdo');
              setCurrentFactIndex(0);
            }}
            onBack={onBack}
          />
        );
      }

      case 'youdo': {
        const youDoFactsWithIndices = factsToPractice.map(factId => ({
          factId,
          factIndex: factToIndex(factId)
        }));
        return (
          <YouDoScreen
            settings={settings}
            factsToPractice={youDoFactsWithIndices}
            onComplete={() => {
              setCurrentScreen('accuracy');
            }}
            onBack={onBack}
          />
        );
      }

      case 'accuracy': {
        // Use ALL track facts for accuracy practice (not just the learning facts)
        return (
          <AccuracyScreen
            settings={settings}
            factsToPractice={getAllTrackFacts()}
            onComplete={() => {
              // AccuracyScreen handles pass/fail via page reload, this is fallback
              window.location.reload();
            }}
            onBack={onBack}
          />
        );
      }

      case 'fluency15': {
        // Use ALL track facts for fluency15 practice
        return (
          <Fluency15Screen
            settings={settings}
            factsToPractice={getAllTrackFacts()}
            onComplete={() => {
              // Fluency15Screen handles pass/fail via page reload, this is fallback
              window.location.reload();
            }}
            onBack={onBack}
          />
        );
      }

      case 'fluency':
        return (
          <FluencyScreen
            settings={settings}
            factsToPractice={factsToPractice.map(factId => ({
              factId,
              factIndex: factToIndex(factId)
            }))}
            onComplete={onComplete}
            onBack={onBack}
          />
        );

      default:
        return null;
    }
  };

  // Handle empty factsToPractice array - navigate back via useEffect to avoid side effects during render
  useEffect(() => {
    if (factsToPractice.length === 0) {
      console.error('AdditionWithin5Practice: factsToPractice array is empty after practice started');
      onBack();
    }
  }, [factsToPractice.length, onBack]);

  // Guard against empty factsToPractice array - return stable fallback UI
  if (factsToPractice.length === 0) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100">
      {renderCurrentScreen()}
    </div>
  );
};

export default AdditionWithin5Practice;
